/**
 * Embeds Noto Sans (SIL OFL) for jsPDF so Romanian and other Latin Extended
 * text renders correctly. Standard Helvetica is limited to PDF built-in encoding.
 *
 * Font files live in `public/fonts/` (downloaded from Google Noto).
 */

const VFS_REGULAR = 'NotoSans-Regular.ttf';
const VFS_BOLD = 'NotoSans-Bold.ttf';
const VFS_ITALIC = 'NotoSans-Italic.ttf';
export const PDF_UNICODE_FAMILY = 'NotoSans';

function uint8ToBase64(bytes) {
  let binary = '';
  const len = bytes.byteLength;
  const chunk = 0x8000;
  for (let i = 0; i < len; i += chunk) {
    binary += String.fromCharCode.apply(null, bytes.subarray(i, Math.min(i + chunk, len)));
  }
  return btoa(binary);
}

let cachedBase64 = null;

async function loadFontBase64s() {
  if (cachedBase64) return cachedBase64;
  const base = typeof process !== 'undefined' && process.env && process.env.PUBLIC_URL ? process.env.PUBLIC_URL : '';
  const prefix = `${base}/fonts`.replace(/\/$/, '');
  const urls = [
    `${prefix}/${VFS_REGULAR}`,
    `${prefix}/${VFS_BOLD}`,
    `${prefix}/${VFS_ITALIC}`,
  ];
  const buffers = await Promise.all(
    urls.map(async (url) => {
      const res = await fetch(url);
      if (!res.ok) throw new Error(`Font fetch ${url}: ${res.status}`);
      return new Uint8Array(await res.arrayBuffer());
    })
  );
  cachedBase64 = buffers.map(uint8ToBase64);
  return cachedBase64;
}

/**
 * @param {import('jspdf').jsPDF} doc
 * @returns {Promise<boolean>} true if Noto Sans is registered on this doc
 */
export async function embedNotoSansForPdf(doc) {
  if (typeof doc.addFileToVFS !== 'function' || typeof doc.addFont !== 'function') {
    return false;
  }
  try {
    const [reg, bold, italic] = await loadFontBase64s();
    doc.addFileToVFS(VFS_REGULAR, reg);
    doc.addFont(VFS_REGULAR, PDF_UNICODE_FAMILY, 'normal');
    doc.addFileToVFS(VFS_BOLD, bold);
    doc.addFont(VFS_BOLD, PDF_UNICODE_FAMILY, 'bold');
    doc.addFileToVFS(VFS_ITALIC, italic);
    doc.addFont(VFS_ITALIC, PDF_UNICODE_FAMILY, 'italic');
    return true;
  } catch (e) {
    if (typeof console !== 'undefined' && console.warn) {
      console.warn('[PDF] Noto Sans could not be embedded; Romanian diacritics may be wrong.', e);
    }
    return false;
  }
}
